const math = require('mathjs');
const _ = require('lodash');
const {
  makeSurfaceFromReading,
  sensorPixelCoordinatesToSurfaceIndex,
} = require('./reading-to-surface');
const { degreesToRadians } = require('./degrees');
const { feedMaskPixelTypes } = require('./feed-masks');

const NARROW_SEARCH_SETTINGS = {
  numRounds: 3,
  panRange: [degreesToRadians(-6), degreesToRadians(12)],
  panSteps: 7,
  tiltRange: [degreesToRadians(-8), degreesToRadians(8)],
  tiltSteps: 7,
  distanceScaleRange: [0.79, 1.0],
  distanceScaleSteps: 4,
};

const DISABLED_SEARCH_SETTINGS = {
  numRounds: 1,
  panRange: [0, 0],
  panSteps: 1,
  tiltRange: [0, 0],
  tiltSteps: 1,
  distanceScaleRange: [1, 1],
  distanceScaleSteps: 1,
};

const MAX_NARROW_SEARCH_PIXELS = 160 * 60;

/**
 * @typedef {Object} SensorImage
 */

/**
 * @param {SensorImage} image
 * @param {FeedMask} feedMask
 * @param {BinGeometry} binGeometry
 * @param {Object} [customSearchSettings]
 * @param {Number} [customSearchSettings.numRounds]  The number of times to refine the search over all parameters
 * @param {[Number,Number]|Number} [customSearchSettings.panRange] The min and max pan values to try in each round in radians or the fixed value to use
 * @param {Number} [customSearchSettings.panSteps]  The number of different pan values to try in each round
 * @param {[Number,Number]|Number} [customSearchSettings.tiltRange] The min and max tilt values to try in each round in radians or the fixed value to use
 * @param {Number} [customSearchSettings.tiltSteps] The number of different tilt values to try in each round
 * @param {[Number,Number]|Number} [customSearchSettings.distanceScaleRange]  The min and max distanceScale values to try in each round or the fixed value to use
 * @param {Number} [customSearchSettings.distanceScaleSteps] The number of different distanceScale values to try in each round
 * @returns {{score: number, distanceScale: number, tilt: number, pan: number}}
 */
exports.cameraRotationDetection = function (image, feedMask, binGeometry, customSearchSettings = {}) {
  const defaultSearchSettings = (image.width * image.height) <= MAX_NARROW_SEARCH_PIXELS
    ? NARROW_SEARCH_SETTINGS
    : DISABLED_SEARCH_SETTINGS;
  const searchSettings = { ...defaultSearchSettings, ..._.omitBy(customSearchSettings, _.isUndefined) };
  let { panRange, panSteps, tiltRange, tiltSteps, distanceScaleRange, distanceScaleSteps } = searchSettings;
  const { numRounds } = searchSettings;

  if (typeof panRange === 'number') {
    panRange = [panRange, panRange];
    panSteps = 1;
  }
  if (typeof tiltRange === 'number') {
    tiltRange = [tiltRange, tiltRange];
    tiltSteps = 1;
  }
  if (typeof distanceScaleRange === 'number') {
    distanceScaleRange = [distanceScaleRange, distanceScaleRange];
    distanceScaleSteps = 1;
  }

  // We're going to search for a distance scale, pan and tilt that gets the best score from our scoring function
  let bests;
  for (let round = 1; round <= numRounds; round++) {
    bests = findBestSettings(binGeometry, image, feedMask,
      distanceScaleRange, distanceScaleSteps,
      panRange, panSteps,
      tiltRange, tiltSteps);

    distanceScaleRange = updateRange(distanceScaleRange, distanceScaleSteps, bests.distanceScale);
    panRange = updateRange(panRange, panSteps, bests.pan);
    tiltRange = updateRange(tiltRange, tiltSteps, bests.tilt);
  }

  return bests;
};

const updateRange = ([min, max], steps, best) => {
  const stepSize = (max - min) / (steps + 1);
  return [best - stepSize, best + stepSize];
};

function computeAnchorPointScore(image, feedMask, surface, binGeometry) {
  if (!binGeometry.isCircular) {
    throw new Error('Only cylinder bins are supported');
  }
  const anchorPoints = memoizedGetPointsOnEdgeOfMask(image, feedMask);
  const anchorDistances = anchorPoints.map(([xIndex, yIndex]) => {
    const index = sensorPixelCoordinatesToSurfaceIndex(xIndex, yIndex, image.width);
    const distance = binGeometry.distanceToBin(surface.pointCloud.point(index));
    return Math.abs(distance);
  });
  const circularBinRadius = binGeometry.bin.dimensions.body.radius;
  return anchorDistances.length !== 0 ?
    Math.max(0, (circularBinRadius - math.mean(anchorDistances)) / circularBinRadius)
    : 1;
}

function computeFeedFitsScore(image, feedMask, surface, binGeometry) {
  let pixelsThatAreWhereTheyShouldBe = 0;
  let totalPixels = 0;
  for (let x = 0; x < feedMask[0].length; x++) {
    for (let y = 0; y < feedMask.length; y++) {
      const index = sensorPixelCoordinatesToSurfaceIndex(x, y, image.width);
      const feedMaskValue = feedMask[y][x];
      if ([feedMaskPixelTypes.FEED, feedMaskPixelTypes.BIN_WALL, feedMaskPixelTypes.OBSTRUCTION].includes(feedMaskValue)) {
        const pixelShouldBeInBin = [feedMaskPixelTypes.FEED, feedMaskPixelTypes.OBSTRUCTION].includes(feedMaskValue);
        if (pixelShouldBeInBin === binGeometry.isPointInsideBin(surface.pointCloud.point(index))) {
          pixelsThatAreWhereTheyShouldBe++;
        }
        totalPixels++;
      }
    }
  }
  return totalPixels > 0 ? pixelsThatAreWhereTheyShouldBe / totalPixels : 1.0;
}

const computeCertaintyScore = ({ pan, tilt, distanceScale }, binGeometry, feedMask) => {
  const expectedRotation = binGeometry.estimatedCameraAngleForBin();
  const expectedDistanceScale = 1;
  const maxAngle = Math.PI / 2;

  const distanceToExpectedSettings = math.norm([
    (expectedRotation.pan - pan) / maxAngle, // unitless
    (expectedRotation.tilt - tilt) / maxAngle, // unitless
    expectedDistanceScale - distanceScale, // unitless
  ]);

  // This value scales from 0 -> 1 where 0 is the expected values and close to 1 are the values that are more unexpected
  const scaledDistanceToExpectedSettings = Math.min(distanceToExpectedSettings / math.norm([1, 1, expectedDistanceScale]), 1);

  const { wallPixelsOnLeft, wallPixelsOnRight } = countWallPixelsBySideOfImage(feedMask);

  // This value scales from 0->1 measuring how confident we are in seeing walls on both sides of the image
  // At 1, both sides of the image have at least 15 pixels of wall visible. At 0, one side has no wall pixels.
  const wallConfidence = Math.min(Math.min(wallPixelsOnLeft, wallPixelsOnRight) / 15, 1);

  // Now combine scaledDistanceToExpectedSettings with the wallConfidence to produce a score ranging from 0->1 where
  // higher numbers have lower distances and higher confidence values.  We are trying to bias the score to picking
  // settings that are "far away" from the expected settings only when the wallConfidence value is high.
  return 1 - ((1 - wallConfidence) * scaledDistanceToExpectedSettings);
};

function countWallPixelsBySideOfImage(feedMask) {
  let wallPixelsOnLeft = 0;
  let wallPixelsOnRight = 0;

  feedMask.forEach(row => {
    for (let i = 0; i < row.length; i++) {
      if (row[i] === 0 /* wall pixel */) {
        if (i < row.length / 2) {
          wallPixelsOnLeft++;
        } else {
          wallPixelsOnRight++;
        }
      }
    }
  });

  return { wallPixelsOnLeft, wallPixelsOnRight };
}

exports.scoreRotation = (pan, tilt, distanceScale, binGeometry, image, feedMask) => {
  if (!binGeometry.isCircular) {
    return 1;
  }
  // For performance's sake we use a memoized version of makeSurfaceFromReading, and since we don't want to modify the memoized instance of the surface we immediately clone it.
  // This gives us a roughly 50x speed up.
  const rawSurface = memoizedMakeSurfaceFromReading(image, feedMask, { distanceScale, pixelTypes: Object.values(feedMaskPixelTypes) });
  const surface = binGeometry.changeSurfaceToBinsCoordinateSystem(rawSurface, tilt, pan);

  // Compute "anchor point score", which is a measure of how nicely the edge of the feed mask intersects the bin. This is a value from 0->1 where 1 is a perfect fit.
  const anchorPointScore = computeAnchorPointScore(image, feedMask, surface, binGeometry);

  // Compute the "feed fits score", which is the percentage of feed pixels that ends up in the bin
  const feedFitsScore = computeFeedFitsScore(image, feedMask, surface, binGeometry);

  // Compute the "certainty score" which scales from 0->1, and scores "smaller" corrections better than larger corrections when the feed mask is mostly feed.
  const certaintyScore = computeCertaintyScore({ pan, tilt, distanceScale }, binGeometry, feedMask);

  return anchorPointScore * feedFitsScore * certaintyScore;
};

const _lastMakeSurfaceCall = {
  arguments: [null, null, null],
  result: null,
};

// We want to cache just the last result in case all the arguments are still the same (but not cache every invocation ever).
// This is how memoize-one works, but memoize-one does NOT work in a mixed CommonJS/ESM environment (like this package needs to do).
// So manually cache the last response
const memoizedMakeSurfaceFromReading = (image, feedMask, options) => {
  if (image === _lastMakeSurfaceCall.arguments[0] && feedMask === _lastMakeSurfaceCall.arguments[1] && _.isEqual(options, _lastMakeSurfaceCall.arguments[2])) {
    return _lastMakeSurfaceCall.result;
  }

  const result = makeSurfaceFromReading(image, feedMask, options);

  _lastMakeSurfaceCall.arguments = [image, feedMask, options];
  _lastMakeSurfaceCall.result = result;

  return result;
};

function findBestSettings(binGeometry, image, feedMask,
  [minDistanceScale, maxDistanceScale], distanceScaleSteps,
  [minPan, maxPan], panSteps,
  [minTilt, maxTilt], tiltSteps) {
  const bests = {
    score: -1,
    pan: 0,
    tilt: 0,
    distanceScale: 1,
  };
  for (let distanceScaleStep = 1; distanceScaleStep <= distanceScaleSteps; distanceScaleStep++) {
    const distanceScale = minDistanceScale + (maxDistanceScale - minDistanceScale) * (distanceScaleStep / (distanceScaleSteps + 1));
    for (let panStep = 1; panStep <= panSteps; panStep++) {
      const pan = minPan + (maxPan - minPan) * (panStep / (panSteps + 1));
      for (let tiltStep = 1; tiltStep <= tiltSteps; tiltStep++) {
        const tilt = minTilt + (maxTilt - minTilt) * (tiltStep / (tiltSteps + 1));
        const score = exports.scoreRotation(pan, tilt, distanceScale, binGeometry, image, feedMask);
        if (score > bests.score) {
          bests.score = score;
          bests.pan = pan;
          bests.tilt = tilt;
          bests.distanceScale = distanceScale;
        }
      }
    }
  }
  return bests;
}

function pixelIsEdgeOfFeed(x, y, feedMask) {
  if (feedMask[y][x] !== feedMaskPixelTypes.FEED) {
    return false;
  }

  const width = feedMask[0].length;
  const height = feedMask.length;

  for (const deltaX of [-1, 1]) {
    for (const deltaY of [-1, 1]) {
      const xNeighbour = x + deltaX;
      const yNeighbour = y + deltaY;

      if (xNeighbour < 0 || xNeighbour >= width || yNeighbour < 0 || yNeighbour >= height) {
        continue;
      }

      if (feedMask[yNeighbour][xNeighbour] === feedMaskPixelTypes.BIN_WALL) {
        return true;
      }
    }
  }

  return false;
}

function getPointsOnEdgeOfMask(reading, feedMask) {
  const points = [];
  for (let x = 0; x < reading.width; x++) {
    for (let y = 0; y < reading.height; y++) {
      if (pixelIsEdgeOfFeed(x, y, feedMask)) {
        points.push([x, y]);
      }
    }
  }

  return points;
}

const _lastGetPointsOnEdgeOfMaskCall = {
  arguments: [null, null],
  result: null,
};
function memoizedGetPointsOnEdgeOfMask(image, feedMask) {
  if (image === _lastGetPointsOnEdgeOfMaskCall.arguments[0] && feedMask === _lastGetPointsOnEdgeOfMaskCall.arguments[1]) {
    return _lastGetPointsOnEdgeOfMaskCall.result;
  }

  const result = getPointsOnEdgeOfMask(image, feedMask);

  _lastGetPointsOnEdgeOfMaskCall.arguments = [image, feedMask];
  _lastGetPointsOnEdgeOfMaskCall.result = result;

  return result;
}

